/**
 * Android's back closes the library layer on top — an open list today, a dialog from stage 5 —
 * instead of leaving the app.
 *
 * @why    A native `<select>` gets this for free: Android shows its own picker and back dismisses
 *         it. The library's Select is a list the page draws, and the Tracker keeps no history of
 *         its own (no router, not one `pushState`), so back with a list open would take the kitchen
 *         OUT of the app — to Chrome's previous page, or closed, in the installed PWA.
 * @rule   A layer that opens adds one history entry; back pops it and closes that layer only,
 *         the one on top: a list open inside a dialog closes, the dialog stays. A layer closed any
 *         other way (an option picked, a tap outside, its X) takes its own entry back out, so the
 *         history is left as it was found and the next back does what it did before.
 * @why    One `popstate` listener for all layers, with a stack, rather than one per layer: the
 *         entry a layer removes itself (`history.back()`) arrives as a `popstate` too, and only
 *         the stack knows it is not a back press.
 */

// Tokens are unique across page loads: a reload keeps `history.state`, and a token that restarted
// at 1 would match the entry a layer open before the reload left behind (the review of stage 4).
const LOAD = Math.random().toString(36).slice(2, 10);
const stack = [];
let ownBacks = 0;
let nextToken = 1;

// An entry left by a layer that was open when the page reloaded: nothing owns it any more.
if (typeof window !== 'undefined' && window.history.state && window.history.state.bzLayer) {
    const rest = { ...window.history.state };
    delete rest.bzLayer;
    window.history.replaceState(Object.keys(rest).length ? rest : null, '');
}

function stopListeningIfIdle() {
    if (!stack.length && ownBacks === 0) window.removeEventListener('popstate', onPopState);
}

function onPopState() {
    if (ownBacks > 0) {
        ownBacks -= 1;
    } else {
        const current = window.history.state && window.history.state.bzLayer;
        // Close every layer whose entry is gone — normally the top one; more if back skipped several.
        while (stack.length && stack[stack.length - 1].token !== current) {
            const layer = stack.pop();
            layer.onBack();
        }
    }
    stopListeningIfIdle();
}

/**
 * Registers a layer that is opening. Call the returned function when it closes by any other
 * means; calling it after back already closed the layer does nothing.
 *
 * @param {() => void} onBack Closes the layer; called when back is pressed with it on top.
 * @returns {() => void} closed — the layer closed on its own.
 */
export function openLayer(onBack) {
    const token = `${LOAD}:${nextToken++}`;
    if (!stack.length) window.addEventListener('popstate', onPopState);
    window.history.pushState({ ...(window.history.state || {}), bzLayer: token }, '');
    stack.push({ token, onBack });

    return function closed() {
        const at = stack.findIndex((layer) => layer.token === token && !layer.gone);
        if (at < 0) return;
        if (at < stack.length - 1) {
            // Not on top: its entry is under someone else's, and cannot be taken out from here. It
            // stays as a closed placeholder, so the back that reaches it costs one press that does
            // nothing — instead of closing the layer above it too.
            stack[at] = { token, gone: true, onBack() {} };
            return;
        }
        stack.pop();
        // Placeholders left under it now have their entries on top: they go with it.
        let behind = 0;
        while (stack.length && stack[stack.length - 1].gone) {
            stack.pop();
            behind += 1;
        }
        if (window.history.state && window.history.state.bzLayer === token) {
            ownBacks += 1;
            window.history.go(-(1 + behind));
        }
        stopListeningIfIdle();
    };
}

/** How many layers are open. For tests. */
export function openLayers() {
    return stack.filter((layer) => !layer.gone).length;
}
